import { useEffect, useState, useCallback } from "react";
import { Box, Flex, Heading, Text, Stack, HStack, Icon, Spinner, Button } from "@chakra-ui/react";
import { Users, ClipboardList, MapPin, Building2, History } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { SegmentedTabs } from "@/components/SegmentedTabs";
import { colors } from "@/theme/colors";

const PAGE = 50;

const CATEGORIES = {
  people: { icon: Users, tint: colors.tealTint, color: colors.tealHover },
  forms: { icon: ClipboardList, tint: colors.resolvedBg, color: colors.resolvedText },
  sites: { icon: MapPin, tint: colors.pendingBg, color: colors.pendingText },
  company: { icon: Building2, tint: colors.cream, color: colors.textSecondary },
};

const ACTION_META = {
  user_invited: { cat: "people", text: (d) => `invited ${d.email || "a user"}${d.role ? ` as ${pretty(d.role)}` : ""}` },
  invite_resent: { cat: "people", text: (d) => `re-sent the invite to ${d.email || "a user"}` },
  membership_attached_existing_account: { cat: "people", text: (d) => `added existing account ${d.email || ""} as ${pretty(d.role || "member")}` },
  invite_blocked_already_member: { cat: "people", text: (d) => `invite blocked — ${d.email || "email"} is already a member` },
  invite_blocked_existing_account: { cat: "people", text: (d) => `invite blocked — ${d.email || "email"} could not be added` },
  invite_resend_blocked_established: { cat: "people", text: (d) => `invite link refused — ${d.email || "user"} already has an account` },
  worker_added: { cat: "people", text: (d) => `added employee ${d.name || ""}` },
  user_deactivated: { cat: "people", text: (d) => `deactivated ${d.name || d.email || "an employee"}` },
  user_reactivated: { cat: "people", text: (d) => `reactivated ${d.name || d.email || "an employee"}` },
  user_removed: { cat: "people", text: (d) => `removed ${d.email || "a user"} from the company` },
  user_role_changed: { cat: "people", text: (d) => `changed ${d.email || "a user"}'s role from ${pretty(d.old)} to ${pretty(d.new)}` },
  password_reset_sent: { cat: "people", text: (d) => `sent a password reset to ${d.email || "a user"}` },
  profile_updated: { cat: "people", text: () => "updated their profile details" },
  assignment_created: { cat: "forms", text: (d) => `assigned "${d.title || "a form"}" to ${(d.workers || []).join(", ") || "workers"}${d.occurrences > 1 ? ` (${d.occurrences} occurrences)` : ""}` },
  assignment_awaiting_signatures: { cat: "forms", text: (d) => `"${d.title || "a form"}" is awaiting additional signatures` },
  signature_added: { cat: "forms", text: (d) => `${d.signer || "someone"} signed "${d.title || "a document"}"` },
  document_acknowledged: { cat: "forms", text: (d) => `${d.worker || "someone"} acknowledged "${d.title || "a document"}"` },
  comment_added: { cat: "forms", text: (d) => `commented on a submission (${d.section || "report"})` },
  status_change: { cat: "forms", text: (d) => `submission status changed from ${pretty(d.from)} to ${pretty(d.to)}` },
  location_added: { cat: "sites", text: (d) => `added location ${d.name || ""}` },
  location_deactivated: { cat: "sites", text: (d) => `deactivated location ${d.name || ""}` },
  location_reactivated: { cat: "sites", text: (d) => `reactivated location ${d.name || ""}` },
  company_deactivated: { cat: "company", text: () => "deactivated the company" },
  company_restored: { cat: "company", text: () => "restored the company" },
  idle_timeout_changed: { cat: "company", text: (d) => `changed the idle sign-out limit to ${d.minutes} minutes` },
};

function pretty(s) {
  return String(s || "").replaceAll("_", " ");
}

function describe(row) {
  const meta = ACTION_META[row.action];
  if (meta) return { cat: meta.cat, text: meta.text(row.details || {}) };
  return { cat: "forms", text: pretty(row.action) };
}

const dayLabel = (ts) => new Date(ts).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" });
const timeLabel = (ts) => new Date(ts).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export default function Activity() {
  const { user, activeCompanyId, isSuperAdmin, companies } = useAuth();
  const companyList = Object.values(companies || {})
    .filter((c) => c.active !== false)
    .sort((a, b) => a.name.localeCompare(b.name));
  const [selected, setSelected] = useState("");
  const companyId = activeCompanyId || selected || companyList[0]?.id || null;
  const [rows, setRows] = useState([]);
  const [actors, setActors] = useState({});
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState("all");

  const fetchPage = useCallback(async (before) => {
    let q = supabase.from("audit_log")
      .select("id,action,details,actor,created_at")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false })
      .limit(PAGE);
    if (before) q = q.lt("created_at", before);
    const { data, error } = await safeQuery(q);
    if (error && !before) {
      // Offline: serve the last-known-good first page instead of an empty feed.
      const snap = cacheGet(user.id, "activity") || {};
      setActors((a) => ({ ...a, ...(snap.actors || {}) }));
      setMore(false);
      setLoadFailed(true);
      return snap.rows || [];
    }
    if (!before) setLoadFailed(false);
    const page = data || [];
    const ids = [...new Set(page.map((r) => r.actor).filter(Boolean))];
    let profMap = {};
    if (ids.length) {
      const { data: profs } = await safeQuery(supabase.from("profiles").select("id,full_name,email").in("id", ids));
      profMap = Object.fromEntries((profs || []).map((p) => [p.id, p.full_name || p.email]));
      setActors((a) => ({ ...a, ...profMap }));
    }
    if (!before) cachePut(user.id, "activity", { rows: page, actors: profMap });
    setMore(page.length === PAGE);
    return page;
  }, [companyId, user.id]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      setLoading(true);
      setRows(await fetchPage(null));
      setLoading(false);
    })();
  }, [companyId, fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    const page = await fetchPage(rows[rows.length - 1]?.created_at);
    setRows((r) => [...r, ...page]);
    setLoadingMore(false);
  };

  const filtered = rows.filter((r) => filter === "all" || describe(r).cat === filter);
  const groups = [];
  filtered.forEach((r) => {
    const day = dayLabel(r.created_at);
    if (!groups.length || groups[groups.length - 1].day !== day) groups.push({ day, items: [] });
    groups[groups.length - 1].items.push(r);
  });

  return (
    <Box data-testid="activity-page">
      <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>Activity</Heading>
      <Text color={colors.textSecondary} mb="5">Your company's recent audit trail — invites, form assignments, sign-offs and site changes, newest first.</Text>

      <Box mb="5">
        <Flex gap="3" align="center" flexWrap="wrap">
          <SegmentedTabs value={filter} onChange={setFilter} tabs={[
            { key: "all", label: "All" },
            { key: "people", label: "People" },
            { key: "forms", label: "Forms" },
            { key: "sites", label: "Sites" },
            { key: "company", label: "Company" },
          ]} />
          {isSuperAdmin && (
            <Box as="select" value={companyId || ""} onChange={(e) => setSelected(e.target.value)}
              px="3" py="2" borderRadius="8px" border="1px solid" borderColor={colors.border}
              bg={colors.white} fontSize="sm" data-testid="activity-company-select">
              {companyList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Box>
          )}
        </Flex>
      </Box>

      {loading ? (
        <Flex minH="30vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>
      ) : filtered.length === 0 ? (
        <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="6" data-testid="activity-empty">
          <HStack gap="2"><Icon as={History} color={colors.textSecondary} /><Text color={colors.textSecondary} fontSize="sm">{loadFailed ? "You're offline — the activity feed will appear once this device has loaded it." : "No activity in this category yet."}</Text></HStack>
        </Box>
      ) : (
        <Stack gap="6">
          {groups.map((g) => (
            <Box key={g.day}>
              <Text fontSize="xs" fontWeight="700" color={colors.textSecondary} textTransform="uppercase" letterSpacing="0.06em" mb="2">{g.day}</Text>
              <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" overflow="hidden">
                {g.items.map((r, i) => {
                  const d = describe(r);
                  const cat = CATEGORIES[d.cat] || CATEGORIES.forms;
                  const who = actors[r.actor] || "Hakina Safety";
                  return (
                    <Flex key={r.id} gap="3" px="4" py="3" align="flex-start"
                      borderTop={i === 0 ? "none" : "1px solid"} borderColor={colors.border}
                      data-testid={`activity-row-${r.id}`}>
                      <Flex w="30px" h="30px" borderRadius="8px" bg={cat.tint} align="center" justify="center" flexShrink={0}>
                        <Icon as={cat.icon} boxSize="3.5" color={cat.color} />
                      </Flex>
                      <Box flex="1" minW="0">
                        <Text fontSize="sm" color={colors.textPrimary}>
                          <Text as="span" fontWeight="600">{who}</Text> {d.text}
                        </Text>
                      </Box>
                      <Text fontSize="xs" color={colors.textSecondary} flexShrink={0}>{timeLabel(r.created_at)}</Text>
                    </Flex>
                  );
                })}
              </Box>
            </Box>
          ))}
          {more && filter === "all" && (
            <Flex justify="center">
              <Button size="sm" variant="outline" onClick={loadMore} loading={loadingMore} data-testid="activity-load-more">Load more</Button>
            </Flex>
          )}
        </Stack>
      )}
    </Box>
  );
}
